// Auto-answer (interactions.json `autoAnswer`): a per-viewer convenience for decisions whose only available choice is a
// single stepless command of a declared kind, such as passing an optional response window with nothing to respond with.
// The client sends exactly the command the player would send by pressing the button; it never decides legality and the
// host still judges it. The preference is local to this browser and on by default; it is also offered in the game settings panel.

import type { ChoiceView } from '../model/choices';

export interface AutoAnswerRule {
  /** Action kind of the choice the client may answer. */
  kind: string;
  /** Wait before sending, the same for every decision. A fast pass still tells other players the viewer had nothing to respond with; the setting says so. */
  delayMs?: number;
  /** Text of the on/off control, for example "Pass automatically when I cannot respond". */
  label?: string;
}

export const AUTO_ANSWER_DELAY_MS = 700;
const MIN_DELAY = 150;
const MAX_DELAY = 5000;

export function autoAnswerDelay(rule: AutoAnswerRule): number {
  return Math.min(MAX_DELAY, Math.max(MIN_DELAY, rule.delayMs ?? AUTO_ANSWER_DELAY_MS));
}

/**
 * The single eligible choice the client may answer, with its rule, or null. Several eligible choices, a step, a
 * parameter form or a confirmation warning always leave the decision to the player.
 */
export function autoAnswerOf(eligible: ChoiceView[], rules: AutoAnswerRule[] | undefined): { choice: ChoiceView; rule: AutoAnswerRule } | null {
  if (!rules?.length || eligible.length !== 1) return null;
  const choice = eligible[0]!;
  const rule = rules.find(r => r.kind === choice.action.kind);
  if (!rule || choice.steps.length > 0 || choice.parameters.length > 0 || choice.confirm) return null;
  return { choice, rule };
}

const key = (gameId: string, kind: string) => `bgc.autoAnswer.${gameId}.${kind}`;

/** On unless the viewer switched it off in this browser (storage can be unavailable). */
export function readAutoAnswer(gameId: string, kind: string): boolean {
  try { return window.localStorage.getItem(key(gameId, kind)) !== '0'; } catch { return true; }
}

export function writeAutoAnswer(gameId: string, kind: string, enabled: boolean): void {
  try { window.localStorage.setItem(key(gameId, kind), enabled ? '1' : '0'); } catch { /* per-viewer convenience only */ }
}
